"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { gridItemVariants, pageVariants, slideFromRightVariants, staggerContainerVariants} from "@/lib/animations";
import PostCard from "@/components/ui/PostCard";
import PostCardSkeleton from "@/components/ui/PostCardSkeleton";
import BlogRightBar, { type BlogRightBarProps } from "@/components/sections/BlogRightBar";
import type { BlogPostCardViewModel,PaginationInfo} from "@/types/blog";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";

interface BlogHomeProps {
    posts: BlogPostCardViewModel[];
    pagination: PaginationInfo;
    language: string;
    defaultLanguage: string;
    sidebar?: BlogRightBarProps;
    paginationBasePath?: string;
}

const buildRange = (current: number, total: number, radius = 2): number[] => {
    const start = Math.max(1, current - radius);
    const end = Math.min(total, current + radius);
    const range: number[] = [];
    for (let i = start; i <= end; i += 1) {
        range.push(i);
    }
    return range;
};

const buildPageHref = (basePath: string, page: number): string => {
    const normalized = basePath.replace(/\/+$/, "") || "/";
    if (page <= 1) {
        return normalized || "/";
    }
    const prefix = normalized === "/" ? "" : normalized;
    return `${prefix}/${page}`.replace(/\/+/g, "/");
};

export default function BlogHome({
    posts,
    pagination,
    sidebar,
    paginationBasePath,
}: BlogHomeProps) {
    const { variables: contextVariables } = useLanguageVariables();
    const variables = contextVariables || {};
    
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };

    const safePosts = Array.isArray(posts) ? posts : [];
    const safePagination: PaginationInfo = pagination ?? {
        page: 1,
        pageSize: safePosts.length || 1,
        pageCount: 1,
        total: safePosts.length || 0,
    };

    const paginationPages = buildRange(safePagination.page, safePagination.pageCount);

    const basePath = paginationBasePath?.replace(/\/+$/, "") || "/";
    const isLoading = safePosts.length === 0;
    
    return (
        <motion.div
            className="w-9/12 mx-auto px-4 py-16"
            initial="hidden"
            animate="visible"
            variants={pageVariants}
        >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 relative">
                <motion.div
                    className="col-span-12"
                    variants={staggerContainerVariants}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {isLoading ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <PostCardSkeleton key={index} index={index} />
                            ))
                        ) : (
                            safePosts.slice(0, 3).map((post, index) => (
                                <PostCard
                                    key={post.id ?? `${post.href}-${index}`}
                                    post={post}
                                    index={index}
                                    isInView={true}
                                />
                            ))
                        )}
                    </div>
                </motion.div>
                <motion.div
                    className="col-span-12 lg:col-span-8"
                    variants={staggerContainerVariants}
                >
                    <div className="flex flex-col gap-8">
                        {isLoading ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <PostCardSkeleton key={index} index={index} />
                            ))
                        ) : (
                            <>
                                { safePosts.slice(4).map((post, index) => (
                                    <PostCard
                                        key={post.id ?? `${post.href}-${index}`}
                                        post={post}
                                        index={index}
                                        isInView={true}
                                    />
                                ))}
                                {safePosts.length === 0 && (
                                    <div className="rounded border border-dashed border-alsero-soft-white bg-white px-8 py-16 text-center text-lg text-alsero-light-gray">
                                        {getVariable('blog_home_no_posts', 'No blog posts matched your filters.')}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <motion.div
                        className={`flex justify-center items-center gap-2 mt-12
                            [&_button]:flex [&_button]:items-center [&_button]:justify-center [&_button]:w-[50px] [&_button]:h-[50px]
                            [&_button]:cursor-pointer
                            [&_button]:border [&_button]:border-alsero-soft-white
                            [&_button]:transform-border [&_button]:ease-in-out [&_button]:duration-300
                            [&_button]:hover:border-alsero-dark-gray
                            [&_button]:text-alsero-light-gray [&_button]:hover:text-alsero-dark-gray
                            [&_button]:data-active=true:border-alsero-first [&_button]:data-active=true:bg-alsero-first [&_button]:data-active=true:text-white`}
                        variants={gridItemVariants}
                    >
                        <motion.div whileHover={{ scale: safePagination.page > 1 ? 1.1 : 1 }} whileTap={{ scale: safePagination.page > 1 ? 0.9 : 1 }}>
                            <Link
                                href={buildPageHref(basePath, safePagination.page - 1)}
                                aria-label={getVariable('blog_home_previous_page', 'Previous page')}
                                className={`flex h-[50px] w-[50px] items-center justify-center border border-alsero-soft-white transition-all duration-300 ${safePagination.page <= 1 ? 'pointer-events-none opacity-50' : 'hover:border-alsero-dark-gray hover:text-alsero-dark-gray'}`}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0.29527 6.7124L5.28685 11.704C5.68054 12.0977 6.31905 12.0977 6.71275 11.704C7.10644 11.3103 7.10644 10.6718 6.71275 10.2781L2.43457 5.99995L6.71275 1.72181C7.10644 1.32812 7.10644 0.689613 6.71275 0.29592C6.31905 -0.0977728 5.68054 -0.0977728 5.28685 0.29592L0.29527 5.2875C-0.0984226 5.68021 -0.0984226 6.3187 0.29527 6.7124ZM6.29707 7.01466L10.26 11.1914C10.6582 11.6105 11.3031 11.6105 11.7013 11.1914C12.0996 10.7722 12.0996 10.0922 11.7013 9.67257L8.45889 6.25525L11.7013 2.83794C12.0996 2.41875 12.0996 1.7383 11.7013 1.31912C11.3031 0.89994 10.6582 0.89994 10.26 1.31912L6.29707 5.49585C5.89887 5.91501 5.89887 6.59499 6.29707 7.01466Z" fill="black" />
                                </svg>
                            </Link>
                        </motion.div>
                        {paginationPages.map((pageNumber) => (
                            <motion.div
                                key={pageNumber}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                data-active={pageNumber === safePagination.page}
                                className="transition-colors duration-300"
                            >
                                <Link
                                    href={buildPageHref(basePath, pageNumber)}
                                    className={`flex h-[50px] w-[50px] items-center justify-center border border-alsero-soft-white ${pageNumber === safePagination.page ? 'border-alsero-first bg-alsero-first text-white' : 'hover:border-alsero-dark-gray hover:text-alsero-dark-gray'}`}
                                >
                                    {pageNumber}
                                </Link>
                            </motion.div>
                        ))}
                        <motion.div whileHover={{ scale: safePagination.page < safePagination.pageCount ? 1.1 : 1 }} whileTap={{ scale: safePagination.page < safePagination.pageCount ? 0.9 : 1 }}>
                            <Link
                                href={buildPageHref(basePath, safePagination.page + 1)}
                                aria-label={getVariable('blog_home_next_page', 'Next page')}
                                className={`flex h-[50px] w-[50px] items-center justify-center border border-alsero-soft-white transition-all duration-300 ${safePagination.page >= safePagination.pageCount ? 'pointer-events-none opacity-50' : 'hover:border-alsero-dark-gray hover:text-alsero-dark-gray'}`}
                            >
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.7047 5.2876L6.71315 0.296002C6.31946 -0.0976908 5.68095 -0.0976908 5.28725 0.296002C4.89356 0.689695 4.89356 1.3282 5.28725 1.7219L9.56543 6.00005L5.28725 10.2782C4.89356 10.6719 4.89356 11.3104 5.28725 11.7041C5.68095 12.0978 6.31946 12.0978 6.71315 11.7041L11.7047 6.7125C12.0984 6.31979 12.0984 5.6813 11.7047 5.2876ZM5.70293 4.98534L1.74004 0.808613C1.34183 0.38945 0.696855 0.38945 0.298652 0.808613C-0.0995507 1.22778 -0.0995507 1.90775 0.298652 2.32743L3.54111 5.74475L0.298652 9.16206C-0.0995507 9.58125 -0.0995507 10.2617 0.298652 10.6809C0.696855 11.1001 1.34183 11.1001 1.74004 10.6809L5.70293 6.50415C6.10113 6.08499 6.10113 5.40501 5.70293 4.98534Z" fill="black" />
                                </svg>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
                {sidebar ? (
                    <BlogRightBar
                        recentPosts={sidebar.recentPosts}
                        categories={sidebar.categories}
                        tags={sidebar.tags}
                        language={sidebar.language}
                        defaultLanguage={sidebar.defaultLanguage}
                        searchUrl={sidebar.searchUrl}
                        translations={sidebar.translations}
                    />
                ) : (
                    <motion.div
                        className="col-span-12 lg:col-span-4 self-start"
                        variants={slideFromRightVariants}
                    />
                )}
            </div>
        </motion.div>
    );
}